import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cancelScheduleVote,
  fetchMySchedules,
  voteSchedule,
} from "../../api/schedule/scheduleApi";
import {
  extractScheduleItems,
  normalizeSchedulePayloadItem,
} from "../../api/schedule/scheduleModel";
import { getMyTeams } from "../../api/team/teamApi";
import UserScheduleCard from "../../components/user-schedule-card/UserScheduleCard";
import useAutoClearMessage from "../../hooks/useAutoClearMessage";

const FALLBACK_CURRENT_USER_ID = "demo-user";
const MY_SCHEDULE_PAGE_SIZE = 50;

function toUserKey(userId) {
  if (userId === null || userId === undefined) {
    return "";
  }
  return String(userId);
}

function getCurrentUserKey(currentUser) {
  return (
    toUserKey(currentUser?.userId) ||
    toUserKey(currentUser?.id) ||
    toUserKey(currentUser?.providerId) ||
    FALLBACK_CURRENT_USER_ID
  );
}

function isVoteOpen(schedule) {
  const now = new Date();
  return (
    new Date(schedule.voteStartAt) <= now && now <= new Date(schedule.voteEndAt)
  );
}

function formatDateLabel(dateValue) {
  return new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(dateValue));
}

function formatDateTime(dateValue) {
  return new Intl.DateTimeFormat("ko-KR", {
    month: "short",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(dateValue));
}

function mergeSchedulesWithTeams(teams, schedules) {
  const teamMap = new Map(teams.map((team) => [String(team.id), team]));

  return schedules
    .map((schedule) => {
      const matchedTeam = teamMap.get(String(schedule.teamId));
      return {
        ...schedule,
        teamId: String(schedule.teamId || matchedTeam?.id || ""),
        teamName: schedule.teamName || matchedTeam?.name || "이름 없는 팀",
        teamLogoUrl: schedule.teamLogoUrl || matchedTeam?.logoUrl || "",
        teamChair: matchedTeam?.chair || "-",
        teamRole: matchedTeam?.role || "MEMBER",
      };
    })
    .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime());
}

function formatLocalDateTimeForQuery(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function ClientSchedulePage({ currentUser }) {
  const currentUserId = useMemo(() => getCurrentUserKey(currentUser), [currentUser]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [teams, setTeams] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("ALL");
  const [votingScheduleIds, setVotingScheduleIds] = useState([]);
  useAutoClearMessage(errorMessage, setErrorMessage);

  const loadUserSchedules = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const [myTeams, myScheduleResponse] = await Promise.all([
        getMyTeams(),
        fetchMySchedules({
          page: 0,
          size: MY_SCHEDULE_PAGE_SIZE,
          from: formatLocalDateTimeForQuery(new Date()),
        }),
      ]);

      setTeams(myTeams);
      const normalizedSchedules = extractScheduleItems(myScheduleResponse).map(
        normalizeSchedulePayloadItem,
      );
      setSchedules(mergeSchedulesWithTeams(myTeams, normalizedSchedules));
    } catch (error) {
      setErrorMessage(error?.message || "내 일정 조회에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserSchedules();
  }, [loadUserSchedules]);

  const filteredSchedules = useMemo(() => {
    if (selectedTeamId === "ALL") {
      return schedules;
    }
    return schedules.filter((schedule) => String(schedule.teamId) === selectedTeamId);
  }, [schedules, selectedTeamId]);

  const groupedSchedules = useMemo(() => {
    const groupedMap = new Map();

    filteredSchedules.forEach((schedule) => {
      const groupKey = new Date(schedule.startsAt).toISOString().slice(0, 10);
      const groupLabel = formatDateLabel(schedule.startsAt);

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, {
          key: groupKey,
          label: groupLabel,
          items: [],
        });
      }

      groupedMap.get(groupKey).items.push(schedule);
    });

    return Array.from(groupedMap.values());
  }, [filteredSchedules]);

  const nextSchedule = filteredSchedules[0] || schedules[0] || null;

  const handleVote = useCallback(
    async (scheduleId, voteValue) => {
      const scheduleKey = String(scheduleId);
      const selectedSchedule = schedules.find(
        (schedule) => String(schedule.id) === scheduleKey,
      );

      if (!selectedSchedule || !isVoteOpen(selectedSchedule)) {
        return;
      }

      if (votingScheduleIds.includes(scheduleKey)) {
        return;
      }

      const currentVote = selectedSchedule?.votes?.[currentUserId] || null;
      const isToggleCancel = currentVote === voteValue;

      setVotingScheduleIds((previous) =>
        previous.includes(scheduleKey) ? previous : [...previous, scheduleKey],
      );

      try {
        if (isToggleCancel) {
          await cancelScheduleVote({
            teamId: selectedSchedule.teamId,
            scheduleId,
          });
        } else {
          await voteSchedule({
            teamId: selectedSchedule.teamId,
            scheduleId,
            voteType: voteValue,
          });
        }

        setSchedules((previous) =>
          previous.map((schedule) => {
            if (String(schedule.id) !== scheduleKey) {
              return schedule;
            }

            if (isToggleCancel) {
              const nextVotes = { ...(schedule.votes || {}) };
              delete nextVotes[currentUserId];
              return {
                ...schedule,
                votes: nextVotes,
              };
            }

            return {
              ...schedule,
              votes: {
                ...(schedule.votes || {}),
                [currentUserId]: voteValue,
              },
            };
          }),
        );
      } catch (error) {
        setErrorMessage(error?.message || "일정 투표 저장에 실패했습니다.");
      } finally {
        setVotingScheduleIds((previous) =>
          previous.filter((id) => id !== scheduleKey),
        );
      }
    },
    [currentUserId, schedules, votingScheduleIds],
  );

  const handleMoveToTeam = useCallback((teamId) => {
    window.location.replace(`/team/${encodeURIComponent(String(teamId))}`);
  }, []);

  return (
    <main
      className="min-h-screen px-4 py-6 md:px-8 md:py-8"
      style={{
        background:
          "radial-gradient(circle at 0% 0%, #dcfce7 0, transparent 28%), radial-gradient(circle at 100% 0%, #dbeafe 0, transparent 34%), linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <div className="mx-auto flex w-full max-w-[980px] flex-col gap-6">
        <section className="rounded-[30px] border border-[#dbe4f4] bg-white/88 p-5 shadow-[0_18px_48px_rgba(15,23,42,0.08)] md:p-7">
          <p className="m-0 inline-block rounded-full bg-[#111827] px-3 py-1 text-xs font-bold tracking-[0.08em] text-white">
            MY SCHEDULE
          </p>
          <h1 className="mt-3 text-[clamp(24px,4.5vw,34px)] font-semibold tracking-[-0.03em] text-[#0f172a]">
            내 일정
          </h1>
          <p className="mt-2 text-sm text-[#475467]">
            로그인한 계정이 속한 모든 팀의 다가오는 일정을 한 화면에서 확인합니다.
          </p>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-[#e4e7ec] bg-[#f8fafc] px-4 py-4">
              <p className="m-0 text-xs font-semibold text-[#667085]">가입 팀</p>
              <p className="mt-2 text-2xl font-bold text-[#101828]">{teams.length}</p>
            </div>
            <div className="rounded-2xl border border-[#e4e7ec] bg-[#f8fafc] px-4 py-4">
              <p className="m-0 text-xs font-semibold text-[#667085]">다가오는 일정</p>
              <p className="mt-2 text-2xl font-bold text-[#101828]">
                {filteredSchedules.length}
              </p>
            </div>
            <div className="rounded-2xl border border-[#e4e7ec] bg-[#f8fafc] px-4 py-4">
              <p className="m-0 text-xs font-semibold text-[#667085]">가장 가까운 일정</p>
              <p className="mt-2 text-sm font-semibold text-[#101828]">
                {nextSchedule
                  ? `${nextSchedule.teamName} · ${formatDateTime(nextSchedule.startsAt)}`
                  : "예정된 일정이 없습니다."}
              </p>
            </div>
          </div>
        </section>

        {teams.length > 0 && (
          <section className="rounded-[28px] border border-[#dbe4f4] bg-white/90 p-4 shadow-[0_16px_36px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSelectedTeamId("ALL")}
                className={`h-10 rounded-full px-4 text-sm font-semibold transition ${
                  selectedTeamId === "ALL"
                    ? "bg-[#111827] text-white"
                    : "border border-[#d0d5dd] bg-white text-[#344054]"
                }`}
              >
                전체 팀
              </button>
              {teams.map((team) => {
                const isSelected = selectedTeamId === String(team.id);
                return (
                  <button
                    key={`schedule-filter-${team.id}`}
                    type="button"
                    onClick={() => setSelectedTeamId(String(team.id))}
                    className={`h-10 rounded-full px-4 text-sm font-semibold transition ${
                      isSelected
                        ? "bg-[#111827] text-white"
                        : "border border-[#d0d5dd] bg-white text-[#344054]"
                    }`}
                  >
                    {team.name}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {isLoading ? (
          <section className="rounded-[28px] border border-[#dbe4f4] bg-white/92 p-6 text-center shadow-[0_16px_36px_rgba(15,23,42,0.06)]">
            <p className="m-0 text-sm font-semibold text-[#344054]">
              내 일정 목록을 불러오고 있습니다.
            </p>
          </section>
        ) : errorMessage ? (
          <section className="rounded-[28px] border border-[#fecdca] bg-white p-6 shadow-[0_16px_36px_rgba(15,23,42,0.06)]">
            <p className="m-0 text-sm font-semibold text-[#b42318]">{errorMessage}</p>
            <button
              type="button"
              onClick={loadUserSchedules}
              className="mt-4 h-10 rounded-lg bg-[#111827] px-4 text-sm font-semibold text-white"
            >
              다시 불러오기
            </button>
          </section>
        ) : teams.length === 0 ? (
          <section className="rounded-[28px] border border-[#dbe4f4] bg-white/92 p-6 shadow-[0_16px_36px_rgba(15,23,42,0.06)]">
            <h2 className="m-0 text-lg font-semibold text-[#101828]">
              아직 소속된 팀이 없습니다.
            </h2>
            <p className="mt-2 text-sm text-[#475467]">
              팀을 찾거나 새로 등록한 뒤, 여기서 모든 팀 일정을 한 번에 볼 수 있습니다.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => window.location.replace("/teams/find")}
                className="h-10 rounded-lg bg-[#111827] px-4 text-sm font-semibold text-white"
              >
                팀 찾기
              </button>
              <button
                type="button"
                onClick={() => window.location.replace("/teams/new")}
                className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054]"
              >
                팀 등록
              </button>
            </div>
          </section>
        ) : filteredSchedules.length === 0 ? (
          <section className="rounded-[28px] border border-[#dbe4f4] bg-white/92 p-6 shadow-[0_16px_36px_rgba(15,23,42,0.06)]">
            <h2 className="m-0 text-lg font-semibold text-[#101828]">
              선택한 범위에 다가오는 일정이 없습니다.
            </h2>
            <p className="mt-2 text-sm text-[#475467]">
              운영진이 새 일정을 등록하면 이 페이지에서 팀별로 함께 보여집니다.
            </p>
          </section>
        ) : (
          <div className="flex flex-col gap-5">
            {groupedSchedules.map((group) => (
              <section key={group.key} className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <h2 className="m-0 text-lg font-semibold text-[#101828]">
                    {group.label}
                  </h2>
                  <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-[#475467]">
                    {group.items.length}개 일정
                  </span>
                </div>

                <div className="grid gap-4">
                  {group.items.map((schedule) => {
                    const matchedTeam =
                      teams.find((team) => String(team.id) === String(schedule.teamId)) || {
                        id: schedule.teamId,
                        name: schedule.teamName,
                        logoUrl: schedule.teamLogoUrl,
                        chair: schedule.teamChair,
                        role: schedule.teamRole,
                      };

                    return (
                      <UserScheduleCard
                        key={`user-schedule-${schedule.teamId}-${schedule.id}`}
                        team={matchedTeam}
                        schedule={schedule}
                        currentUserId={currentUserId}
                        onVote={handleVote}
                        isVoting={votingScheduleIds.includes(String(schedule.id))}
                        onOpenTeam={handleMoveToTeam}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export default ClientSchedulePage;
